import DeleteIcon from '@mui/icons-material/Delete'
import { Box, IconButton, Typography } from '@mui/material'
import { message, Modal, Table, TableColumnsType } from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'

import CircularLoading from '../../../../../components/CircularProgress'
import Pagination from '../../../../../components/Pagination'
import { ONLY_DATE_TIME_FORMAT } from '../../../../../constants/global'
import { axiosInstance } from '../../../../../hooks/useAxios'

export interface IAiMessage {
  _id: string
  merchant: string
  message: string
  objectId: string
  createdAt: string
  updatedAt: string
}

export interface IAiMessagesResponse {
  records: IAiMessage[]
  count: number
  page: number
  perPage: number
}

export interface IAiMessageFilter {
  page: number
  perPage: number
  search?: string
}

interface IAiMessagesTableProps {
  isLoading: boolean
  data: IAiMessage[]
  total: number
  filter: IAiMessageFilter
  onPageChange: (filter: IAiMessageFilter) => void
  onRefresh: () => void
}

const AiMessagesTable = (props: IAiMessagesTableProps) => {
  const { isLoading, data, total, filter, onPageChange, onRefresh } = props
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  const handleDelete = (record: IAiMessage) => {
    Modal.confirm({
      title: 'ยืนยันการลบข้อความ AI',
      content: `ลบข้อความ: ${record.message}`,
      onOk: async () => {
        setIsDeleting(true)
        try {
          await axiosInstance.delete(`/ai-messages/${record.objectId}`)
          message.success('ลบข้อมูลสำเร็จ')
          onRefresh()
        } catch (e) {
          message.error('ลบข้อมูลไม่สำเร็จ')
        }
        setIsDeleting(false)
      },
    })
  }

  const columns: TableColumnsType<
    IAiMessage & { key: string; numberRow: number }
  > = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => (
        <Box>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            textAlign={'center'}
          >
            {numberRow}
          </Typography>
        </Box>
      ),
    },
    // {
    //   title: 'ร้านค้า',
    //   dataIndex: 'merchant',
    //   key: 'merchant',
    //   align: 'left' as const,
    //   width: 160,
    //   render: (merchant: string) => (
    //     <Typography variant="subtitle1" color="text.secondary">{merchant}</Typography>
    //   ),
    // },
    {
      title: 'ข้อความ',
      dataIndex: 'message',
      key: 'message',
      align: 'left' as const,
      render: (msg: string) => (
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{ whiteSpace: 'pre-line' }}
        >
          {msg}
        </Typography>
      ),
    },
    {
      title: 'วันที่เพิ่ม',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      align: 'center' as const,
      render: (createdAt: string) => (
        <Typography variant="subtitle2" color="text.secondary">
          {dayjs(createdAt).format(ONLY_DATE_TIME_FORMAT)}
        </Typography>
      ),
    },
    {
      title: 'วันที่แก้ไข',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      align: 'center' as const,
      render: (updatedAt: string) => (
        <Typography variant="subtitle2" color="text.secondary">
          {dayjs(updatedAt).format(ONLY_DATE_TIME_FORMAT)}
        </Typography>
      ),
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 80,
      align: 'center' as const,
      render: (_: any, record: IAiMessage) => (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <IconButton onClick={() => handleDelete(record)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <>
      <CircularLoading isLoading={isLoading || isDeleting} />
      <Pagination
        total={total}
        current={filter.page}
        pageSize={filter.perPage}
        onChange={(page, perPage) => onPageChange({ ...filter, page, perPage })}
        mb={2}
      />
      <Table
        size={'middle'}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map((item: IAiMessage, index: number) => ({
          ...item,
          key: item.objectId,
          numberRow:
            index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
        }))}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page}
        pageSize={filter.perPage}
        onChange={(page, perPage) => onPageChange({ ...filter, page, perPage })}
      />
    </>
  )
}

export default AiMessagesTable
